/**
 * Lazy-loads the full Lucide icon set (~1500 icons) from CDN.
 * Only fetched when user searches — not bundled.
 */

const LUCIDE_CDN_URL =
  'https://cdn.jsdelivr.net/npm/lucide-static@0.460.0/icon-nodes.json';

type LucideNode = [string, Record<string, string>];
type LucideIconNodes = Record<string, LucideNode[]>;

let cachedIcons: LucideIconNodes | null = null;
let fetchPromise: Promise<LucideIconNodes | null> | null = null;

/**
 * Fetch the full Lucide icon-nodes.json (once, cached).
 * Returns null if fetch fails.
 */
export const fetchLucideIcons = (): Promise<LucideIconNodes | null> => {
  if (cachedIcons) return Promise.resolve(cachedIcons);
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch(LUCIDE_CDN_URL)
    .then((res) => {
      if (!res.ok) return null;
      return res.json() as Promise<LucideIconNodes>;
    })
    .then((data) => {
      cachedIcons = data;
      return data;
    })
    .catch(() => {
      fetchPromise = null;
      return null;
    });

  return fetchPromise;
};

/** Convert a single Lucide icon node array to an SVG element string. */
const nodeToSvgContent = (nodes: LucideNode[]): string => {
  return nodes
    .map(([tag, attrs]) => {
      const attrStr = Object.entries(attrs)
        .map(([k, v]) => `${k}="${v}"`)
        .join(' ');
      return `<${tag} ${attrStr} />`;
    })
    .join('');
};

/** Generate a full <svg> string for clipboard copy. */
export const lucideToSvgHtml = (
  iconName: string,
  nodes: LucideNode[]
): string => {
  const content = nodeToSvgContent(nodes);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${content}</svg>`;
};

/** Search Lucide icons by name substring (case-insensitive). */
export const searchLucideIcons = (
  icons: LucideIconNodes,
  query: string
): string[] => {
  const q = query.toLowerCase();
  return Object.keys(icons).filter((name) => name.includes(q));
};

/** Get the cached icon nodes (for rendering previews). */
export const getCachedIcons = (): LucideIconNodes | null => cachedIcons;
